import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetcher } from "./fetcher";
import { getSession, useSession } from "next-auth/react";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";

export const useDiscussListAll = (opts?: {
    /** relatedStock 생략 — 목록·GEO SSR 용 (검색은 full 유지) */
    lite?: boolean;
}): [any, boolean] => {
    const lite = !!opts?.lite;
    const { data: session }: any = useSession();
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "list", session?.user?.email || null, lite ? "lite" : "full"],
        queryFn: () => {
            const params = new URLSearchParams();
            if (session?.user?.email) params.set("email", session.user.email);
            if (lite) params.set("lite", "true");
            const qs = params.toString();
            return fetcher(qs ? `/api/discuss/list?${qs}` : `/api/discuss/list`);
        },
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const isValid = isLoading;

    return [data, isValid];
}

//특정 아젠다에 대한 관련 아젠다 리스트
export const useRelatedAgenda = (discussId: number): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "related", discussId],
        queryFn: () => fetcher(`/api/discuss/relatedagenda?discussId=${discussId}`),
    });
    const isValid = isLoading;
    return [data, isValid];
}

{/**
    before: 투표 진행중인 토론 리스트
    after : 메인 화면 최신순 토론 리스트 5개 (투표 종료 여부 X)
    */}
export const useOngoingDiscuss = (enable = true): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "list", "ongoing"],
        queryFn: () => fetcher(`/api/discuss/list?ongoing=true`),
        enabled: enable,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const isValid = isLoading;
    return [data, isValid];
}

// 관리자가 지정한 메인 리서치 1개
export const useMainResearch = (): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "main"],
        queryFn: () => fetcher(`/api/discuss/main`),
    });
    const isValid = isLoading;
    return [data, isValid];
}

// 인기 리서치 리스트
export const usePopularDiscuss = (): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "popular"],
        queryFn: () => fetcher(`/api/discuss/popular`),
    });
    const isValid = isLoading;
    return [data, isValid];
}

// 종목으로 모아보기
export const useCollectByStock = (): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "collect", "stock"],
        queryFn: () => fetcher(`/api/discuss/collect/stock`),
    });
    const isValid = isLoading;
    return [data, isValid];
}

// 키워드 리스트
export const useCollectByKeyword = (): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "collect", "keyword"],
        queryFn: () => fetcher(`/api/discuss/collect/keyword`),
    });
    const isValid = isLoading;
    return [data, isValid];
}
// 특정 키워드 리서치 리서치
export const useCollectByKeywordDetail = (id: string): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "collect", "keyword", id],
        queryFn: () => fetcher(`/api/discuss/collect/keyword?id=${id}`),
    });
    const isValid = isLoading;
    return [data, isValid];
}

export const useIsUserVote = ({ discussId }: { discussId: number }): [any, boolean] => {
    const { data: session }: any = useSession();
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "vote", discussId, session?.user?.email],
        queryFn: () => fetcher(`/api/discuss/vote?email=${session?.user?.email}&discussId=${discussId}`),
        enabled: !!(session?.user?.email && discussId),
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const isValid = isLoading;
    return [data, isValid];
}

export const useNotPublishDiscuss = (): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "list", "admin"],
        queryFn: () => fetcher(`/api/discuss/list?admin=true`),
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const isValid = isLoading;
    return [data, isValid];
}

export const useTestAgendaList = (): [any, boolean] => {

    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "list", "test"],
        queryFn: () => fetcher(`/api/discuss/list?test=true`),
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const isValid = isLoading;
    return [data, isValid];
}

interface DiscussDetailData {
    totalVotes: number;
    options: Array<{ value: string; votes: number;[key: string]: any }>;
    userChoice?: string;
    userChoiceSymbol?: string;
    isClosed?: boolean;
    [key: string]: any;
}

export const useDiscussDetail = ({ discussId, setAnimation, setAlarmTrigger, email, isLoginUser, isTest }: { discussId: number, setAnimation?: any, setAlarmTrigger?: any, email?: string, isLoginUser: boolean, isTest?: boolean }): [any, boolean, any, any] => {
    const router = useRouter();
    const queryClient = useQueryClient();

    const queryKey = ["discuss", "detail", discussId, email || null];
    const { data, isLoading, refetch } = useQuery<DiscussDetailData>({
        queryKey,
        queryFn: () => {
            const isTestPage = router.query.test && router.query.test === 'true';
            const url = email
                ? `/api/discuss/${discussId}?email=${email}${isTestPage ? '&test=true' : ''}`
                : `/api/discuss/${discussId}${isTestPage ? '?test=true' : ''}`;
            return fetcher(url);
        },
        enabled: !!discussId,
        staleTime: 1000, // 1분
        refetchOnWindowFocus: false,
    });



    const voteMutation = useMutation({
        mutationFn: async ({ value, symbol }: { value: string, symbol: string }) => {
            const latestSession = await getSession();
            if (!latestSession?.user?.email) {
                throw new Error("로그인이 필요합니다");
            }

            const response = await fetch(`/api/discuss/vote`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    discussId,
                    email: latestSession.user.email,
                    symbol
                }),
            });

            if (response.status !== 200) {
                const error = await response.json();
                throw new Error(error?.message || "투표 실패");
            }

            return response.json();
        },
        onMutate: async ({ value, symbol }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey });

            // Snapshot the previous value
            const previousData = queryClient.getQueryData<DiscussDetailData>(queryKey);

            // Optimistically update to the new value
            if (previousData) {
                const updatedData: DiscussDetailData = {
                    ...previousData,
                    userChoice: value,
                    userChoiceSymbol: symbol,
                    totalVotes: previousData.totalVotes + 1,
                    options: previousData.options.map((option: any) => {
                        if (option.value === value) {
                            return {
                                ...option,
                                votes: option.votes + 1
                            }
                        }
                        return option;
                    }),
                };
                queryClient.setQueryData(queryKey, updatedData);
            }

            return { previousData };
        },
        onError: (err, variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context?.previousData) {
                queryClient.setQueryData(queryKey, context.previousData);
            }
            Sentry.captureException(`투표 실패: ${err}`);
            console.error("투표 실패", err);
        },
        onSettled: () => {
            // Always refetch after error or success
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey });
            }, 500);
        },
    });

    const submitVote = async (value: string, symbol: string, success?: () => void, fail?: (reason?: string) => void) => {
        const latestSession = await getSession();
        if (!latestSession?.user?.email) {
            return router.push(`/login?from=${router.asPath}`);
        }

        if (setAnimation) setAnimation(true);

        // 1초 대기
        const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
        await sleep(50);

        try {
            await voteMutation.mutateAsync({ value, symbol });
            success && success();
        } catch (error) {
            fail && fail(`backend_${(error as any)?.status || 500}`);
            console.error("투표 처리 중 오류:", error);
        }
    };

    const mutate = () => {
        return refetch();
    };

    const isValid = isLoading;
    return [data, isValid, submitVote, mutate];
}


// 브리핑 날짜 조회(light)
export const useBriefingDate = (): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["briefing", "date"],
        queryFn: () => fetcher(`/api/briefing?type=date`),
    });
    const isValid = isLoading;
    return [data, isValid];
}

// 브리핑 전체 데이터 조회
export const useBriefingData = (): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["briefing", "data"],
        queryFn: () => fetcher(`/api/briefing`),
    });
    const isValid = isLoading;
    return [data, isValid];
}

// 관심 뉴스레이더
export const useInterestDiscuss = (): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["discuss", "interest"],
        queryFn: () => fetcher(`/api/discuss/interest`),
    });
    const isValid = isLoading;
    return [data, isValid];
}